import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  LogOut, Plus, Edit, Trash2, X, Loader2, Lock, ArrowLeft, Calendar, User as UserIcon, Upload, Image as ImageIcon
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper: turn relative /api/uploads paths into absolute URLs
const resolveImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
};

export const AdminPage = () => {
  const navigate = useNavigate();

  const [authToken, setAuthToken] = useState(localStorage.getItem('admin_token'));
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '', excerpt: '', content: '', image: '', category: '', author: 'Admin'
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image must be smaller than 10MB');
      return;
    }

    setUploadError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setBlogForm({ ...blogForm, image: response.data.url });
    } catch (error) {
      setUploadError(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const isAuthenticated = !!authToken;

  // Verify token on mount
  useEffect(() => {
    if (authToken) {
      verifyToken();
      fetchBlogs();
    }
  }, []);

  const verifyToken = async () => {
    try {
      await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
    } catch (error) {
      // Token invalid, clear it
      localStorage.removeItem('admin_token');
      setAuthToken(null);
    }
  };

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, credentials);
      const token = response.data.access_token;
      localStorage.setItem('admin_token', token);
      setAuthToken(token);
      setCredentials({ username: '', password: '' });
      await fetchBlogs();
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAuthToken(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    try {
      await axios.delete(`${API_URL}/blogs/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      await fetchBlogs();
    } catch (error) {
      alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSubmitBlog = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      if (editingBlog) {
        await axios.put(`${API_URL}/blogs/${editingBlog.id}`, blogForm, { headers });
      } else {
        await axios.post(`${API_URL}/blogs`, blogForm, { headers });
      }
      await fetchBlogs();
      setShowBlogForm(false);
      setEditingBlog(null);
      setBlogForm({ title: '', excerpt: '', content: '', image: '', category: '', author: 'Admin' });
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const openEditForm = (blog) => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title, excerpt: blog.excerpt, content: blog.content,
      image: blog.image, category: blog.category, author: blog.author
    });
    setShowBlogForm(true);
  };

  const openCreateForm = () => {
    setEditingBlog(null);
    setBlogForm({ title: '', excerpt: '', content: '', image: '', category: '', author: 'Admin' });
    setShowBlogForm(true);
  };

  // === LOGIN VIEW ===
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E0F4F8] via-[#F5F5F0] to-[#D5F0E0] flex items-center justify-center p-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl"
          data-testid="admin-login-page"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#87CEEB]/20 flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#87CEEB]" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
            Admin Portal
          </h1>
          <p className="text-gray-500 text-sm text-center mb-8">
            The Hygiene Company — Blog Management
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <input
                data-testid="login-username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] focus:border-transparent outline-none"
                placeholder="Enter username"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] focus:border-transparent outline-none"
                placeholder="Enter password"
                required
              />
            </div>

            {loginError && (
              <p className="text-red-500 text-sm" data-testid="login-error">{loginError}</p>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-[#101010] text-primary rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="mt-6 w-full flex items-center justify-center gap-2 text-gray-500 text-sm hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to website
          </button>
        </motion.div>
      </div>
    );
  }

  // === ADMIN DASHBOARD ===
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5F0] via-white to-[#E0F4F8]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-xs text-gray-500">The Hygiene Company</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="view-site-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              View Site
            </button>
            <button
              onClick={handleLogout}
              className="text-sm text-red-600 hover:text-red-700 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Manage Blogs</h2>
            <p className="text-sm text-gray-500 mt-1">
              Share challenges, processes, and achievements from the field
            </p>
          </div>
          <button
            data-testid="create-blog-btn"
            onClick={openCreateForm}
            className="px-5 py-2.5 bg-[#87CEEB] text-white rounded-lg font-medium hover:bg-[#6BB6D9] transition-colors flex items-center gap-2 shadow-md"
          >
            <Plus className="w-4 h-4" />
            New Blog
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#87CEEB]" />
          </div>
        ) : blogs.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
            <p className="text-gray-500">No blogs yet. Create your first one!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {blogs.map((blog) => (
              <div
                key={blog.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow"
                data-testid={`admin-blog-${blog.id}`}
              >
                <img src={resolveImageUrl(blog.image)} alt={blog.title} className="w-full h-40 object-cover" />
                <div className="p-5">
                  <span className="inline-block text-[#87CEEB] text-xs px-3 py-1 bg-[#87CEEB]/10 rounded-full mb-3">
                    {blog.category}
                  </span>
                  <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{blog.title}</h3>
                  <p className="text-gray-500 text-sm line-clamp-2 mb-3">{blog.excerpt}</p>
                  <div className="flex items-center gap-3 text-gray-400 text-xs mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {new Date(blog.date).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <UserIcon className="w-3 h-3" /> {blog.author}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditForm(blog)}
                      className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center justify-center gap-2 transition-colors"
                      data-testid={`edit-blog-${blog.id}`}
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(blog.id)}
                      className="flex-1 py-2 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium text-red-600 flex items-center justify-center gap-2 transition-colors"
                      data-testid={`delete-blog-${blog.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create / Edit Blog Modal */}
      {showBlogForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowBlogForm(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingBlog ? 'Edit Blog' : 'Create New Blog'}
              </h2>
              <button
                onClick={() => setShowBlogForm(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitBlog} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  data-testid="blog-title-input"
                  type="text"
                  value={blogForm.title}
                  onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt (short summary)</label>
                <textarea
                  data-testid="blog-excerpt-input"
                  value={blogForm.excerpt}
                  onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content (full blog body)</label>
                <textarea
                  data-testid="blog-content-input"
                  value={blogForm.content}
                  onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
                
                {/* Image preview */}
                {blogForm.image && (
                  <div className="mb-3 relative inline-block">
                    <img
                      src={resolveImageUrl(blogForm.image)}
                      alt="Preview"
                      className="h-40 rounded-lg border border-gray-200 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setBlogForm({ ...blogForm, image: '' })}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md"
                      data-testid="remove-image-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Upload from computer */}
                <label
                  htmlFor="image-upload-input"
                  className={`flex items-center justify-center gap-2 w-full py-3 px-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    uploading
                      ? 'border-gray-300 bg-gray-50 cursor-wait'
                      : 'border-[#87CEEB] bg-[#87CEEB]/5 hover:bg-[#87CEEB]/10 text-[#0a7ba8]'
                  }`}
                  data-testid="upload-image-label"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                      <span className="text-sm text-gray-500">Uploading…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        {blogForm.image ? 'Replace image' : 'Upload from your computer'}
                      </span>
                    </>
                  )}
                </label>
                <input
                  id="image-upload-input"
                  data-testid="image-upload-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="hidden"
                />

                {uploadError && (
                  <p className="text-red-500 text-xs mt-2" data-testid="upload-error">{uploadError}</p>
                )}

                {/* OR paste URL */}
                <div className="mt-3">
                  <label className="block text-xs text-gray-500 mb-1">or paste an image URL</label>
                  <input
                    data-testid="blog-image-input"
                    type="url"
                    value={blogForm.image && !blogForm.image.startsWith('/api/uploads/') ? blogForm.image : ''}
                    onChange={(e) => setBlogForm({ ...blogForm, image: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] outline-none text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <input
                    data-testid="blog-category-input"
                    type="text"
                    value={blogForm.category}
                    onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                    placeholder="Achievement, Challenge, Process"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                  <input
                    data-testid="blog-author-input"
                    type="text"
                    value={blogForm.author}
                    onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#87CEEB] outline-none"
                    required
                  />
                </div>
              </div>

              <button
                data-testid="blog-submit-btn"
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-[#101010] text-primary rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingBlog ? 'Update Blog' : 'Publish Blog'}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};
